<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/interceptor.services';
import type { IProduct } from '@/types/product.types';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';
import { TranslationService } from '@/services/translation.services';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  products: IProduct[];
  commerceId: string;
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

// Monté uniquement quand le slide-over est affiché (v-if côté parent) :
// toujours "ouvert" tant que ce composant existe.
useBodyScrollLock(ref(true));

// ── Étape : 1 = config groupe, 2 = choix produits ─────────────────────────
const step = ref<1 | 2>(1);

// ── Traduction (backend interne) ────────────────────────────────────────── */
async function translateAll(
  src: 'fr' | 'en' | 'nl',
  get: () => string,
  set: (l: 'fr' | 'en' | 'nl', v: string) => void,
  busy: { value: boolean },
) {
  const text = get();
  if (!text.trim() || busy.value) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(text, src);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== src)
      .forEach((l) => set(l, translated[l]));
  } finally {
    busy.value = false;
  }
}

// ── Champs du groupe ───────────────────────────────────────────────────────
const groupTitleLang = ref<'fr' | 'en' | 'nl'>('fr');
const groupTitleFr = ref('');
const groupTitleEn = ref('');
const groupTitleNl = ref('');
const groupTitleTranslating = ref(false);
function onGroupTitleBlur(lang: 'fr' | 'en' | 'nl') {
  if (groupTitleLang.value !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => groupTitleFr.value,
    en: () => groupTitleEn.value,
    nl: () => groupTitleNl.value,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') groupTitleFr.value = v;
      if (l === 'en') groupTitleEn.value = v;
      if (l === 'nl') groupTitleNl.value = v;
    },
    groupTitleTranslating,
  );
}

const chooseType = ref<'multiple' | 'unique'>('unique');
const minQty = ref<number | null>(null);
const maxQty = ref<number | null>(null);
const extraType = ref<'side-dish' | 'supplements' | 'proposal'>('supplements');
const required = ref(false);

interface OptionRow {
  contentFr: string;
  contentEn: string;
  contentNl: string;
  lang: 'fr' | 'en' | 'nl';
  translating: boolean;
  price: number;
}
const options = ref<OptionRow[]>([
  {
    contentFr: '',
    contentEn: '',
    contentNl: '',
    lang: 'fr',
    translating: false,
    price: 0,
  },
]);
function onOptionBlur(row: OptionRow, lang: 'fr' | 'en' | 'nl') {
  if (row.lang !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => row.contentFr,
    en: () => row.contentEn,
    nl: () => row.contentNl,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') row.contentFr = v;
      if (l === 'en') row.contentEn = v;
      if (l === 'nl') row.contentNl = v;
    },
    {
      get value() {
        return row.translating;
      },
      set value(x: boolean) {
        row.translating = x;
      },
    },
  );
}

// ── Produits sélectionnés ──────────────────────────────────────────────────
const selectedIds = ref<Set<string>>(new Set());
const prodSearch = ref('');

const availableProds = computed(() => {
  const q = prodSearch.value.trim().toLowerCase();
  return props.products.filter(
    (p) => !p.disabledAt && (!q || p.designation.toLowerCase().includes(q)),
  );
});

function toggleProd(id: string) {
  if (selectedIds.value.has(id)) selectedIds.value.delete(id);
  else selectedIds.value.add(id);
  selectedIds.value = new Set(selectedIds.value);
}

// ── Options ────────────────────────────────────────────────────────────────
function addOption() {
  options.value.push({
    contentFr: '',
    contentEn: '',
    contentNl: '',
    lang: 'fr',
    translating: false,
    price: 0,
  });
}
function removeOption(i: number) {
  options.value.splice(i, 1);
}

const canGoStep2 = computed(
  () =>
    groupTitleFr.value.trim() !== '' &&
    options.value.some((o) => o.contentFr.trim() !== ''),
);

// ── Save ───────────────────────────────────────────────────────────────────
const saving = ref(false);
const error = ref('');

async function save() {
  error.value = '';
  const validOpts = options.value.filter((o) => o.contentFr.trim() !== '');
  if (
    !groupTitleFr.value.trim() ||
    validOpts.length === 0 ||
    selectedIds.value.size === 0
  )
    return;

  saving.value = true;
  try {
    const payload = {
      title: {
        fr: groupTitleFr.value.trim(),
        en: groupTitleEn.value.trim(),
        nl: groupTitleNl.value.trim(),
      },
      chooseType: chooseType.value,
      extraType: extraType.value,
      required: required.value,
      ...(chooseType.value === 'multiple'
        ? {
            minQty: minQty.value ?? undefined,
            maxQty: maxQty.value ?? undefined,
          }
        : {}),
      option: validOpts.map((o) => ({
        type: 'custom',
        content: {
          fr: o.contentFr.trim(),
          en: o.contentEn.trim(),
          nl: o.contentNl.trim(),
        },
        price: o.price || null,
      })),
    };
    await Promise.all(
      [...selectedIds.value].map((pid) =>
        axiosInstance.post(`product/${pid}/extras`, payload),
      ),
    );
    emit('saved');
    emit('close');
  } catch (e: any) {
    error.value = e?.response?.data?.message ?? t('common.errorOccurred');
  } finally {
    saving.value = false;
  }
}

// reset quand le composant s'ouvre
watch(
  () => props.commerceId,
  () => {
    step.value = 1;
    groupTitleLang.value = 'fr';
    groupTitleFr.value = '';
    groupTitleEn.value = '';
    groupTitleNl.value = '';
    chooseType.value = 'unique';
    extraType.value = 'supplements';
    required.value = false;
    minQty.value = null;
    maxQty.value = null;
    options.value = [
      {
        contentFr: '',
        contentEn: '',
        contentNl: '',
        lang: 'fr',
        translating: false,
        price: 0,
      },
    ];
    selectedIds.value = new Set();
    prodSearch.value = '';
    error.value = '';
  },
);
</script>

<template>
  <!-- Overlay -->
  <Teleport to="body">
    <div class="caog__overlay" @click.self="emit('close')">
      <div class="caog__panel">
        <!-- Header -->
        <div class="caog__hd">
          <div class="caog__hd-left">
            <button class="caog__close" @click="emit('close')">
              <i class="pi pi-arrow-left" />
            </button>
            <div>
              <p class="caog__title">{{ t('optionGroup.title') }}</p>
              <p class="caog__sub">
                {{ t('optionGroup.step', { step, total: 2 }) }}
              </p>
            </div>
          </div>
          <div class="caog__steps">
            <span
              class="caog__step"
              :class="{ 'caog__step--active': step === 1 }"
            >
              <i class="pi pi-sliders-h" /> {{ t('optionGroup.stepConfig') }}
            </span>
            <i class="pi pi-chevron-right caog__step-sep" />
            <span
              class="caog__step"
              :class="{ 'caog__step--active': step === 2 }"
            >
              <i class="pi pi-tag" /> {{ t('optionGroup.stepProducts') }}
            </span>
          </div>
        </div>

        <!-- ── Étape 1 : Config ─────────────────────────── -->
        <div v-if="step === 1" class="caog__body">
          <div class="caog__field">
            <div class="caog__i18n-hd">
              <label class="caog__label">{{
                t('optionGroup.groupName')
              }}</label>
              <div class="caog__lang-toggle">
                <button
                  v-for="lang in ['fr', 'en', 'nl'] as const"
                  :key="lang"
                  type="button"
                  class="caog__lang-btn"
                  :class="{ 'caog__lang-btn--active': groupTitleLang === lang }"
                  @click="groupTitleLang = lang"
                >
                  {{ lang.toUpperCase() }}
                </button>
              </div>
            </div>
            <div class="caog__i18n-row">
              <div class="caog__i18n-field">
                <span
                  class="caog__i18n-badge"
                  :class="{
                    'caog__i18n-badge--active': groupTitleLang === 'fr',
                  }"
                  >FR</span
                >
                <input
                  v-model="groupTitleFr"
                  class="caog__input"
                  :placeholder="t('optionGroup.groupNamePlaceholder')"
                  @blur="onGroupTitleBlur('fr')"
                />
              </div>
              <div class="caog__i18n-field">
                <span
                  class="caog__i18n-badge"
                  :class="{
                    'caog__i18n-badge--active': groupTitleLang === 'en',
                  }"
                  >EN</span
                >
                <input
                  v-model="groupTitleEn"
                  class="caog__input"
                  placeholder="Name in English…"
                  @blur="onGroupTitleBlur('en')"
                />
                <i
                  v-if="groupTitleTranslating && groupTitleLang !== 'en'"
                  class="pi pi-spin pi-spinner caog__i18n-spin"
                />
              </div>
              <div class="caog__i18n-field">
                <span
                  class="caog__i18n-badge"
                  :class="{
                    'caog__i18n-badge--active': groupTitleLang === 'nl',
                  }"
                  >NL</span
                >
                <input
                  v-model="groupTitleNl"
                  class="caog__input"
                  placeholder="Naam…"
                  @blur="onGroupTitleBlur('nl')"
                />
                <i
                  v-if="groupTitleTranslating && groupTitleLang !== 'nl'"
                  class="pi pi-spin pi-spinner caog__i18n-spin"
                />
              </div>
            </div>
          </div>

          <div class="caog__row-2">
            <div class="caog__field">
              <label class="caog__label">{{ t('product.choiceType') }}</label>
              <div class="caog__seg">
                <button
                  class="caog__seg-btn"
                  :class="{ 'caog__seg-btn--on': chooseType === 'unique' }"
                  @click="chooseType = 'unique'"
                >
                  {{ t('product.unique') }}
                </button>
                <button
                  class="caog__seg-btn"
                  :class="{ 'caog__seg-btn--on': chooseType === 'multiple' }"
                  @click="chooseType = 'multiple'"
                >
                  {{ t('product.multiple') }}
                </button>
              </div>
            </div>
            <div v-if="chooseType === 'multiple'" class="caog__field">
              <label class="caog__label">{{ t('product.qtyMinMax') }}</label>
              <div class="caog__row-2">
                <input
                  v-model.number="minQty"
                  type="number"
                  min="0"
                  class="caog__input"
                  :placeholder="t('product.minPlaceholder')"
                />
                <input
                  v-model.number="maxQty"
                  type="number"
                  min="0"
                  class="caog__input"
                  :placeholder="t('product.maxPlaceholder')"
                />
              </div>
            </div>
            <div class="caog__field">
              <label class="caog__label">{{ t('product.extraType') }}</label>
              <select v-model="extraType" class="caog__select">
                <option value="supplements">
                  {{ t('product.typeSupplement') }}
                </option>
                <option value="side-dish">
                  {{ t('product.typeSideDish') }}
                </option>
                <option value="proposal">
                  {{ t('product.typeProposal') }}
                </option>
              </select>
            </div>
          </div>

          <div class="caog__field">
            <label class="caog__label caog__label--check">
              <input
                type="checkbox"
                v-model="required"
                class="caog__checkbox"
              />
              {{ t('product.requiredLabel') }}
            </label>
          </div>

          <!-- Options -->
          <div class="caog__field">
            <label class="caog__label">{{ t('product.groupOptions') }}</label>
            <div class="caog__opts-list">
              <div v-for="(opt, i) in options" :key="i" class="caog__opt-card">
                <div class="caog__i18n-hd">
                  <span class="caog__opt-num"
                    >{{ t('optionGroup.optionN') }} {{ i + 1 }}</span
                  >
                  <div class="caog__lang-toggle">
                    <button
                      v-for="lang in ['fr', 'en', 'nl'] as const"
                      :key="lang"
                      type="button"
                      class="caog__lang-btn"
                      :class="{ 'caog__lang-btn--active': opt.lang === lang }"
                      @click="opt.lang = lang"
                    >
                      {{ lang.toUpperCase() }}
                    </button>
                  </div>
                  <button
                    v-if="options.length > 1"
                    class="caog__rm-opt"
                    style="margin-left: auto"
                    @click="removeOption(i)"
                  >
                    <i class="pi pi-times" />
                  </button>
                </div>
                <div class="caog__i18n-row">
                  <div class="caog__i18n-field">
                    <span
                      class="caog__i18n-badge"
                      :class="{ 'caog__i18n-badge--active': opt.lang === 'fr' }"
                      >FR</span
                    >
                    <input
                      v-model="opt.contentFr"
                      class="caog__input caog__input--opt"
                      @blur="onOptionBlur(opt, 'fr')"
                    />
                  </div>
                  <div class="caog__i18n-field">
                    <span
                      class="caog__i18n-badge"
                      :class="{ 'caog__i18n-badge--active': opt.lang === 'en' }"
                      >EN</span
                    >
                    <input
                      v-model="opt.contentEn"
                      class="caog__input caog__input--opt"
                      placeholder="English…"
                      @blur="onOptionBlur(opt, 'en')"
                    />
                    <i
                      v-if="opt.translating && opt.lang !== 'en'"
                      class="pi pi-spin pi-spinner caog__i18n-spin"
                    />
                  </div>
                  <div class="caog__i18n-field">
                    <span
                      class="caog__i18n-badge"
                      :class="{ 'caog__i18n-badge--active': opt.lang === 'nl' }"
                      >NL</span
                    >
                    <input
                      v-model="opt.contentNl"
                      class="caog__input caog__input--opt"
                      placeholder="Nederlands…"
                      @blur="onOptionBlur(opt, 'nl')"
                    />
                    <i
                      v-if="opt.translating && opt.lang !== 'nl'"
                      class="pi pi-spin pi-spinner caog__i18n-spin"
                    />
                  </div>
                  <div class="caog__price-wrap">
                    <span class="caog__price-sym">€</span>
                    <input
                      v-model.number="opt.price"
                      type="number"
                      min="0"
                      step="0.01"
                      class="caog__input caog__input--price"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
            <button class="caog__add-opt" @click="addOption">
              <i class="pi pi-plus" /> {{ t('optionGroup.addOption') }}
            </button>
          </div>
        </div>

        <!-- ── Étape 2 : Produits ───────────────────────── -->
        <div v-else class="caog__body">
          <div class="caog__prod-search-wrap">
            <i class="pi pi-search caog__prod-search-ico" />
            <input
              v-model="prodSearch"
              class="caog__input caog__prod-search"
              :placeholder="t('optionGroup.searchProduct')"
            />
          </div>

          <p class="caog__prod-hint">
            {{ t('optionGroup.selectHint', { count: selectedIds.size }) }}
          </p>

          <div class="caog__prod-list">
            <div
              v-for="p in availableProds"
              :key="p.id"
              class="caog__prod-row"
              :class="{ 'caog__prod-row--selected': selectedIds.has(p.id) }"
              @click="toggleProd(p.id)"
            >
              <div class="caog__prod-ico">
                <img
                  v-if="p.image?.info?.secureUrl"
                  :src="p.image.info.secureUrl"
                  class="caog__prod-img"
                />
                <i v-else class="pi pi-box" />
              </div>
              <div class="caog__prod-info">
                <span class="caog__prod-name">{{ p.designation }}</span>
                <span class="caog__prod-price"
                  >{{ parseFloat(p.price as any).toFixed(2) }} €</span
                >
              </div>
              <div class="caog__prod-check">
                <i
                  v-if="selectedIds.has(p.id)"
                  class="pi pi-check-circle caog__prod-check-ico--on"
                />
                <i v-else class="pi pi-circle caog__prod-check-ico--off" />
              </div>
            </div>
            <div v-if="availableProds.length === 0" class="caog__prod-empty">
              <i class="pi pi-box" />
              <p>{{ t('optionGroup.noProduct') }}</p>
            </div>
          </div>

          <p v-if="error" class="caog__error">{{ error }}</p>
        </div>

        <!-- Footer -->
        <div class="caog__ft">
          <button
            v-if="step === 1"
            class="caog__btn caog__btn--ghost"
            @click="emit('close')"
          >
            {{ t('product.cancel') }}
          </button>
          <button
            v-if="step === 2"
            class="caog__btn caog__btn--ghost"
            @click="step = 1"
          >
            {{ t('common.back') }}
          </button>

          <button
            v-if="step === 1"
            class="caog__btn caog__btn--primary"
            :disabled="!canGoStep2"
            @click="step = 2"
          >
            {{ t('common.next') }} <i class="pi pi-arrow-right" />
          </button>
          <button
            v-else
            class="caog__btn caog__btn--primary"
            :disabled="saving || selectedIds.size === 0"
            @click="save"
          >
            <i v-if="saving" class="pi pi-spinner pi-spin" />
            {{ saving ? t('product.saving') : t('optionGroup.createGroup') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Overlay */
.caog__overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
}

/* Panel slide-over */
.caog__panel {
  width: 520px;
  max-width: 100vw;
  background: var(--nxt-surface, #fff);
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.18);
}
[data-theme='dark'] .caog__panel {
  background: var(--nxt-surface-low, #141618);
}

/* Header */
.caog__hd {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.caog__hd-left {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}
.caog__close {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface);
  transition: background 0.15s;
}
.caog__close:hover {
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.05));
}
.caog__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0;
}
.caog__sub {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  margin: 0.1rem 0 0;
}

.caog__steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.caog__step {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  transition: all 0.15s;
}
.caog__step--active {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
}
[data-theme='dark'] .caog__step--active {
  background: var(--nxt-on-surface, #e2e4e5);
  color: var(--nxt-surface-low, #141618);
}
.caog__step-sep {
  font-size: 0.625rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.4;
}

/* Body */
.caog__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Fields */
.caog__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.caog__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.caog__label--check {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.caog__checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: var(--nxt-on-surface, #111);
  cursor: pointer;
}
.caog__input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.02));
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.caog__input:focus {
  outline: none;
  border-color: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .caog__input {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

.caog__select {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.02));
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
}
[data-theme='dark'] .caog__select {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

.caog__row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

/* Segmented control */
.caog__seg {
  display: flex;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  overflow: hidden;
}
.caog__seg-btn {
  flex: 1;
  padding: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-variant);
  transition: all 0.15s;
}
.caog__seg-btn--on {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
}
[data-theme='dark'] .caog__seg-btn--on {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}

/* Options list */
.caog__opts-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.caog__opt-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.caog__opt-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.625rem;
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.02));
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
[data-theme='dark'] .caog__opt-card {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
.caog__opt-num {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}
.caog__input--opt {
  flex: 1;
}

/* i18n : sélecteur de langue + champs FR/EN/NL */
.caog__i18n-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.caog__lang-toggle {
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  padding: 0.1875rem;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .caog__lang-toggle {
  background: rgba(255, 255, 255, 0.06);
}
.caog__lang-btn {
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  border: none;
  background: transparent;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.caog__lang-btn--active {
  background: #fff;
  color: #0b1320;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
[data-theme='dark'] .caog__lang-btn--active {
  background: #2a2d33;
  color: #fff;
}
.caog__i18n-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.caog__i18n-field {
  flex: 1;
  min-width: 8rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  position: relative;
}
.caog__i18n-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.375rem;
  font-size: 0.625rem;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
}
[data-theme='dark'] .caog__i18n-badge {
  background: rgba(255, 255, 255, 0.08);
}
.caog__i18n-badge--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .caog__i18n-badge--active {
  background: #fff;
  color: #0b1320;
}
.caog__i18n-spin {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
}
.caog__price-wrap {
  position: relative;
  width: 90px;
  flex-shrink: 0;
}
.caog__price-sym {
  position: absolute;
  left: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.caog__input--price {
  padding-left: 1.5rem;
  width: 100%;
}
.caog__rm-opt {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: transparent;
  cursor: pointer;
  color: var(--nxt-on-surface-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.caog__rm-opt:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}
.caog__add-opt {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.625rem;
  border: 1px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: transparent;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  font-family: inherit;
  transition: all 0.15s;
  margin-top: 0.25rem;
}
.caog__add-opt:hover {
  border-color: var(--nxt-on-surface);
  color: var(--nxt-on-surface);
}

/* Produits step */
.caog__prod-search-wrap {
  position: relative;
}
.caog__prod-search-ico {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
}
.caog__prod-search {
  padding-left: 2.5rem;
}
.caog__prod-hint {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
}

.caog__prod-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  overflow-y: auto;
}
.caog__prod-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  background: var(--nxt-surface, #fff);
  cursor: pointer;
  transition: all 0.15s;
}
.caog__prod-row:hover {
  border-color: rgba(0, 0, 0, 0.2);
}
.caog__prod-row--selected {
  border-color: var(--nxt-on-surface, #111);
  background: rgba(0, 0, 0, 0.03);
}
[data-theme='dark'] .caog__prod-row {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .caog__prod-row--selected {
  border-color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.06);
}

.caog__prod-ico {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.04));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--nxt-on-surface-variant);
}
.caog__prod-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.caog__prod-info {
  flex: 1;
  min-width: 0;
}
.caog__prod-name {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.caog__prod-price {
  display: block;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  margin-top: 0.1rem;
}
.caog__prod-check {
  flex-shrink: 0;
  font-size: 1.1rem;
}
.caog__prod-check-ico--on {
  color: var(--nxt-on-surface, #111);
}
.caog__prod-check-ico--off {
  color: var(--nxt-outline-variant, rgba(0, 0, 0, 0.2));
}
.caog__prod-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
}

/* Error */
.caog__error {
  font-size: 0.8125rem;
  color: #dc2626;
  background: #fee2e2;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
}

/* Footer */
.caog__ft {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}
.caog__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
}
.caog__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.caog__btn--ghost {
  background: transparent;
  border-color: var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  color: var(--nxt-on-surface);
}
.caog__btn--ghost:hover:not(:disabled) {
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.04));
}
.caog__btn--primary {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
}
.caog__btn--primary:hover:not(:disabled) {
  opacity: 0.85;
}
[data-theme='dark'] .caog__btn--primary {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}
</style>
